import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Users, UserPlus, Crown, Shield, User, Trash2, 
  Mail, Copy, Check, RefreshCw, Settings, BarChart3, Link2, Share2,
  Bot, Headphones, Building2, RotateCcw, Plus, Clock, Palmtree, Edit2, Globe
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleIcons = {
  owner: Crown,
  admin: Shield,
  sales_manager: Users,
  sales_rep: User
};

const roleColors = {
  owner: 'bg-amber-500',
  admin: 'bg-purple-500',
  sales_manager: 'bg-blue-500',
  sales_rep: 'bg-green-500'
};

const teamTypeIcons = {
  ai_agents: Bot,
  sales_agents: Users,
  telesales: Headphones,
  general: Building2
};

const teamTypeColors = {
  ai_agents: 'bg-violet-500',
  sales_agents: 'bg-green-500',
  telesales: 'bg-blue-500',
  general: 'bg-slate-500'
};

const TeamManagement = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [allTeams, setAllTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [teamTypes, setTeamTypes] = useState([]);
  const [userRole, setUserRole] = useState('viewer');
  const [userPermissions, setUserPermissions] = useState({});
  const [leadsSummary, setLeadsSummary] = useState([]);
  const [activeTab, setActiveTab] = useState('teams');
  
  // View specific team members
  const [viewingTeam, setViewingTeam] = useState(null);
  const [viewingTeamMembers, setViewingTeamMembers] = useState([]);
  const [showViewTeamDialog, setShowViewTeamDialog] = useState(false);
  
  // Dialogs
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showCreateTeamDialog, setShowCreateTeamDialog] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showEditMemberDialog, setShowEditMemberDialog] = useState(false);
  const [selectedTeamForAdd, setSelectedTeamForAdd] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  
  // Form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('sales_rep');
  const [teamName, setTeamName] = useState('');
  const [teamType, setTeamType] = useState('general');
  const [inviting, setInviting] = useState(false);
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  
  // Add User form state
  const [selectedUserId, setSelectedUserId] = useState('');
  const [addUserRole, setAddUserRole] = useState('sales_rep');
  const [workingHoursStart, setWorkingHoursStart] = useState('09:00');
  const [workingHoursEnd, setWorkingHoursEnd] = useState('18:00');
  const [isOnLeave, setIsOnLeave] = useState(false);
  const [roundRobinEnabled, setRoundRobinEnabled] = useState(true);
  const [addingUser, setAddingUser] = useState(false);
  const [preferredLanguages, setPreferredLanguages] = useState([]);
  
  // Available languages for selection
  const availableLanguages = [
    { code: 'en', name: 'English' },
    { code: 'ar', name: 'Arabic' },
    { code: 'ru', name: 'Russian' },
    { code: 'zh', name: 'Chinese' },
    { code: 'hi', name: 'Hindi' },
    { code: 'ur', name: 'Urdu' },
    { code: 'fr', name: 'French' },
    { code: 'es', name: 'Spanish' },
    { code: 'de', name: 'German' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'fa', name: 'Farsi' },
    { code: 'tl', name: 'Tagalog' },
  ];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Progressive — each section paints as it arrives. The follow-on
  // "current team" call still waits for the first batch to land (it
  // doesn't make sense without it), but invitations + leads-summary
  // inside the inner if block also paint independently.
  const fetchData = () => {
    const primary = [
      axios.get(`${API}/teams/all?token=${token}`)
        .then((r) => setAllTeams(r.data.teams || []))
        .catch((e) => console.error('[TeamManagement] teams failed:', e)),
      axios.get(`${API}/teams/roles?token=${token}`)
        .then((r) => setRoles(Array.isArray(r.data) ? r.data : (r.data.roles || [])))
        .catch((e) => console.error('[TeamManagement] roles failed:', e)),
      axios.get(`${API}/teams/types?token=${token}`)
        .then((r) => setTeamTypes(r.data.types || []))
        .catch((e) => console.error('[TeamManagement] types failed:', e)),
      axios.get(`${API}/accounts/users?token=${token}`)
        .then((r) => setAllUsers(r.data.users || []))
        .catch((e) => console.error('[TeamManagement] users failed:', e)),
      axios.get(`${API}/teams/current?token=${token}`)
        .then((r) => {
          if (r.data.success && r.data.team) {
            setTeam(r.data.team);
            setMembers(r.data.members || []);
            setUserRole(r.data.user_role);
            setUserPermissions(r.data.user_permissions || {});
            // Background fan-out for the nested pair — no need to await
            axios.get(`${API}/teams/invitations?token=${token}`)
              .then((res) => setInvitations(res.data.invitations || []))
              .catch((e) => console.error('[TeamManagement] invitations failed:', e));
            axios.get(`${API}/teams/leads-summary?token=${token}`)
              .then((res) => setLeadsSummary(res.data.summary || []))
              .catch((e) => console.error('[TeamManagement] leads-summary failed:', e));
          }
        })
        .catch((e) => console.error('[TeamManagement] current team failed:', e)),
    ];
    let done = false;
    const off = () => { if (!done) { done = true; setLoading(false); } };
    const safety = setTimeout(off, 3000);
    Promise.race(primary).then(off);
    Promise.allSettled(primary).finally(() => clearTimeout(safety));
  };

  const handleCreateTeam = async () => {
    if (!teamName.trim()) {
      toast.error('Please enter a team name');
      return;
    }
    
    setCreatingTeam(true);
    try {
      const response = await axios.post(`${API}/teams?token=${token}`, {
        name: teamName,
        team_type: teamType
      });
      
      if (response.data.success) {
        toast.success(`Team "${teamName}" created successfully!`);
        setShowCreateTeamDialog(false);
        setTeamName('');
        setTeamType('general');
        fetchData();
      } else {
        toast.error(response.data.error || 'Failed to create team');
      }
    } catch (error) {
      toast.error('Failed to create team');
    } finally {
      setCreatingTeam(false);
    }
  };

  const openAddUserDialog = (teamData) => {
    setSelectedTeamForAdd(teamData);
    setSelectedUserId('');
    setAddUserRole('sales_rep');
    setWorkingHoursStart('09:00');
    setWorkingHoursEnd('18:00');
    setIsOnLeave(false);
    setRoundRobinEnabled(true);
    setPreferredLanguages([]);
    setShowAddUserDialog(true);
  };

  const viewTeamMembers = async (teamData) => {
    try {
      const response = await axios.get(`${API}/teams/${teamData.id}/members?token=${token}`);
      if (response.data.success) {
        setViewingTeam(response.data.team);
        setViewingTeamMembers(response.data.members || []);
        setShowViewTeamDialog(true);
      }
    } catch (error) {
      toast.error('Failed to load team members');
    }
  };

  const fetchTeamMembers = async (teamId) => {
    try {
      const response = await axios.get(`${API}/teams/${teamId}/members?token=${token}`);
      if (response.data.success) {
        setViewingTeamMembers(response.data.members || []);
      }
    } catch (error) {
      console.error('Failed to refresh team members');
    }
  };

  const handleAddUserToTeam = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }
    
    setAddingUser(true);
    try {
      const response = await axios.post(
        `${API}/teams/${selectedTeamForAdd.id}/members?token=${token}`,
        {
          user_id: selectedUserId,
          role: addUserRole,
          round_robin_enabled: roundRobinEnabled,
          working_hours_start: workingHoursStart,
          working_hours_end: workingHoursEnd,
          is_on_leave: isOnLeave,
          preferred_languages: preferredLanguages
        }
      );
      
      if (response.data.success) {
        toast.success(response.data.message || 'User added to team successfully!');
        setShowAddUserDialog(false);
        fetchData();
      } else {
        toast.error(response.data.error || 'Failed to add user');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user to team');
    } finally {
      setAddingUser(false);
    }
  };

  const openEditMemberDialog = (member) => {
    setEditingMember(member);
    setWorkingHoursStart(member.working_hours_start || '09:00');
    setWorkingHoursEnd(member.working_hours_end || '18:00');
    setIsOnLeave(member.is_on_leave || false);
    setRoundRobinEnabled(member.round_robin_enabled !== false);
    setPreferredLanguages(member.preferred_languages || []);
    setShowEditMemberDialog(true);
  };

  const handleUpdateMemberSettings = async () => {
    if (!editingMember) return;
    
    try {
      const response = await axios.put(
        `${API}/teams/members/${editingMember.user_id}/settings?token=${token}`,
        {
          working_hours_start: workingHoursStart,
          working_hours_end: workingHoursEnd,
          is_on_leave: isOnLeave,
          round_robin_enabled: roundRobinEnabled,
          preferred_languages: preferredLanguages
        }
      );
      
      if (response.data.success) {
        toast.success('Member settings updated');
        setShowEditMemberDialog(false);
        setEditingMember(null);
        fetchData();
        // Refresh the viewing team members if dialog is open
        if (viewingTeam) {
          fetchTeamMembers(viewingTeam.id);
        }
      } else {
        toast.error(response.data.error || 'Failed to update settings');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member settings');
    }
  };

  const handleRemoveFromViewingTeam = async (userId, userName) => {
    if (!viewingTeam) return;
    
    if (!window.confirm(`Are you sure you want to remove ${userName} from the team?`)) {
      return;
    }
    
    try {
      const response = await axios.delete(
        `${API}/teams/${viewingTeam.id}/members/${userId}?token=${token}`
      );
      
      if (response.data.success) {
        toast.success(`${userName} removed from team`);
        // Immediately update local state for instant UI feedback
        setViewingTeamMembers(prev => prev.filter(m => m.user_id !== userId));
        // Also refresh from server to ensure consistency
        await fetchData();
      } else {
        toast.error(response.data.error || 'Failed to remove member');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setInviting(true);
    try {
      const response = await axios.post(`${API}/teams/members/invite?token=${token}`, {
        email: inviteEmail,
        role: inviteRole
      });
      
      if (response.data.success) {
        toast.success(`Invitation created for ${inviteEmail}`);
        setShowInviteDialog(false);
        
        if (response.data.invitation_link) {
          setGeneratedLink(response.data.invitation_link);
          setShowLinkDialog(true);
        }
        
        setInviteEmail('');
        fetchData();
      } else {
        toast.error(response.data.error || 'Failed to create invitation');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleToggleRoundRobin = async (memberId, currentState) => {
    try {
      const response = await axios.put(
        `${API}/teams/members/${memberId}/round-robin?token=${token}`,
        { enabled: !currentState }
      );
      
      if (response.data.success) {
        toast.success(`Round-robin ${!currentState ? 'enabled' : 'disabled'}`);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update round-robin');
    }
  };

  const handleToggleLeaveStatus = async (memberId, currentState) => {
    try {
      const response = await axios.put(
        `${API}/teams/members/${memberId}/settings?token=${token}`,
        { is_on_leave: !currentState }
      );
      
      if (response.data.success) {
        toast.success(`Leave status ${!currentState ? 'enabled' : 'disabled'}`);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update leave status');
    }
  };

  const copyGeneratedLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      toast.success('Invitation link copied to clipboard!');
      setCopiedLink('generated');
      setTimeout(() => setCopiedLink(null), 2000);
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      const response = await axios.put(
        `${API}/teams/members/${memberId}/role?token=${token}`,
        { role: newRole }
      );
      
      if (response.data.success) {
        toast.success('Role updated successfully');
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!confirm(`Are you sure you want to remove ${memberName} from the team?`)) {
      return;
    }
    
    try {
      const response = await axios.delete(
        `${API}/teams/members/${memberId}?token=${token}`
      );
      
      if (response.data.success) {
        toast.success('Member removed');
        // Immediately update local state for instant UI feedback
        setViewingTeamMembers(prev => prev.filter(m => m.user_id !== memberId));
        await fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (!confirm(`Are you sure you want to delete team "${teamName}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/teams/${teamId}?token=${token}`);
      if (response.data.success) {
        toast.success('Team deleted successfully');
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await axios.post(`${API}/teams/invitations/${invitationId}/cancel?token=${token}`);
      toast.success('Invitation cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  const copyInviteLink = (invToken) => {
    const link = `${window.location.origin}/join-team?token=${invToken}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(invToken);
    toast.success('Invitation link copied!');
    setTimeout(() => setCopiedLink(null), 2000);
  };

  // Get users not already in a team (for add user dropdown)
  const getAvailableUsers = () => {
    if (!selectedTeamForAdd) return allUsers;
    
    const teamMemberIds = members.map(m => m.user_id);
    return allUsers.filter(u => !teamMemberIds.includes(u.id));
  };

  const canInvite = userPermissions.team?.includes('invite') || userRole === 'owner' || userRole === 'admin';
  const canChangeRoles = userPermissions.team?.includes('change_roles') || userRole === 'owner' || userRole === 'admin';
  const canRemove = userPermissions.team?.includes('remove') || userRole === 'owner' || userRole === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="team-loading">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-primary">Team Management</h1>
          <p className="text-slate-500">
            Manage teams, members, and lead assignment
          </p>
        </div>
        <Dialog open={showCreateTeamDialog} onOpenChange={setShowCreateTeamDialog}>
          <DialogTrigger asChild>
            <Button className="bg-accent hover:bg-accent/90" data-testid="create-team-btn">
              <Plus className="mr-2" size={16} />
              Create Team
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Team</DialogTitle>
              <DialogDescription>
                Create a team and assign a type for the lead qualification workflow
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Team Name</Label>
                <Input
                  placeholder="e.g., Sales Team Dubai"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  data-testid="team-name-input"
                />
              </div>
              <div>
                <Label>Team Type</Label>
                <Select value={teamType} onValueChange={setTeamType}>
                  <SelectTrigger data-testid="team-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {teamTypes.map(type => {
                      const TypeIcon = teamTypeIcons[type.id] || Building2;
                      return (
                        <SelectItem key={type.id} value={type.id}>
                          <div className="flex items-center gap-2">
                            <TypeIcon size={16} className="text-slate-500" />
                            <span>{type.name}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {teamTypes.find(t => t.id === teamType)?.description}
                </p>
              </div>
              <Button 
                onClick={handleCreateTeam} 
                disabled={creatingTeam}
                className="w-full"
                data-testid="submit-create-team-btn"
              >
                {creatingTeam ? 'Creating...' : 'Create Team'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Lead Qualification Flow Info */}
      <Card className="bg-gradient-to-r from-violet-50 to-blue-50 border-violet-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-violet-100 rounded-lg">
              <RotateCcw className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">Lead Qualification Workflow</h3>
              <p className="text-sm text-slate-600 mt-1">
                New leads are automatically assigned to <strong>AI Agents</strong> for initial contact. 
                Based on interest level, leads are routed via round-robin to:
              </p>
              <div className="flex flex-wrap gap-4 mt-3">
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-500 text-white">Interested</Badge>
                  <span className="text-sm text-slate-600">→ Sales Agents (Pre-Qualified)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-500 text-white">Not Interested</Badge>
                  <span className="text-sm text-slate-600">→ Telesales (Under Qualification)</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="teams" data-testid="teams-tab">
            <Building2 className="mr-2" size={16} />
            All Teams ({allTeams.length})
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="members-tab">
            <Users className="mr-2" size={16} />
            My Team Members
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="stats-tab">
            <BarChart3 className="mr-2" size={16} />
            Statistics
          </TabsTrigger>
        </TabsList>

        {/* All Teams Tab */}
        <TabsContent value="teams" className="space-y-4">
          {allTeams.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No Teams Yet</h3>
                <p className="text-slate-500 mb-4">
                  Create your first team to start managing leads
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allTeams.map((t) => {
                const TypeIcon = teamTypeIcons[t.team_type] || Building2;
                const typeColor = teamTypeColors[t.team_type] || 'bg-slate-500';
                
                return (
                  <Card 
                    key={t.id} 
                    className="hover:shadow-md transition-shadow"
                    data-testid={`team-card-${t.id}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 ${typeColor} rounded-lg flex items-center justify-center`}>
                            <TypeIcon size={20} className="text-white" />
                          </div>
                          <div>
                            <CardTitle className="text-base">{t.name}</CardTitle>
                            <Badge variant="outline" className="mt-1 text-xs">
                              {t.team_type_info?.name || t.team_type || 'General'}
                            </Badge>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTeam(t.id, t.name);
                          }}
                          data-testid={`delete-team-${t.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-sm mb-3">
                        <span className="text-slate-500">
                          <Users className="inline mr-1" size={14} />
                          {t.member_count || 0} members
                        </span>
                        <span className="text-slate-400 text-xs">
                          Max: {t.max_members || 50}
                        </span>
                      </div>
                      {t.owner_name && (
                        <p className="text-xs text-slate-400 mb-2">
                          Owner: {t.owner_name}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => viewTeamMembers(t)}
                          data-testid={`view-team-${t.id}`}
                        >
                          <Users className="mr-2" size={14} />
                          View
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          className="flex-1 bg-accent hover:bg-accent/90"
                          onClick={() => openAddUserDialog(t)}
                          data-testid={`add-user-to-team-${t.id}`}
                        >
                          <UserPlus className="mr-2" size={14} />
                          Add
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* My Team Members Tab */}
        <TabsContent value="members" className="space-y-4">
          {!team ? (
            <Card className="text-center py-12">
              <CardContent>
                <p className="text-slate-500">You are not part of any team yet.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Team Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold">{team.name}</h2>
                  <p className="text-sm text-slate-500">
                    {members.length} of {team.max_members || 50} members
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => openAddUserDialog(team)}
                    data-testid="add-user-btn"
                  >
                    <UserPlus className="mr-2" size={16} />
                    Add User
                  </Button>
                  {canInvite && (
                    <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
                      <DialogTrigger asChild>
                        <Button className="bg-accent hover:bg-accent/90" data-testid="invite-btn">
                          <Mail className="mr-2" size={16} />
                          Invite via Email
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Invite Team Member</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <Label>Email Address</Label>
                            <Input
                              type="email"
                              placeholder="colleague@company.com"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              data-testid="invite-email-input"
                            />
                          </div>
                          <div>
                            <Label>Role</Label>
                            <Select value={inviteRole} onValueChange={setInviteRole}>
                              <SelectTrigger data-testid="invite-role-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {roles.filter(r => r.id !== 'owner').map(role => (
                                  <SelectItem key={role.id} value={role.id}>
                                    <div className="flex items-center gap-2">
                                      <span>{role.name}</span>
                                      <span className="text-xs text-slate-500">- {role.description}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button 
                            onClick={handleInviteMember} 
                            disabled={inviting}
                            className="w-full"
                            data-testid="send-invite-btn"
                          >
                            {inviting ? 'Sending...' : 'Send Invitation'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>

              {/* Members List */}
              <Card data-testid="members-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users size={20} />
                    Team Members
                  </CardTitle>
                  <CardDescription>
                    Manage working hours, leave status, and round-robin for automatic lead assignment
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {members.map((member) => {
                      const RoleIcon = roleIcons[member.role] || User;
                      const isCurrentUser = member.user_id === user?.id;
                      const isOwner = member.role === 'owner';
                      const roundRobinOn = member.round_robin_enabled !== false;
                      const onLeave = member.is_on_leave === true;
                      
                      return (
                        <div 
                          key={member.id}
                          className={`flex items-center justify-between p-4 rounded-lg border ${onLeave ? 'bg-amber-50 border-amber-200' : 'border-slate-200 hover:bg-slate-50'}`}
                          data-testid={`member-${member.user_id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full ${roleColors[member.role]} flex items-center justify-center`}>
                              <RoleIcon size={18} className="text-white" />
                            </div>
                            <div>
                              <p className="font-medium text-sm flex items-center gap-2">
                                {member.user_name}
                                {isCurrentUser && <span className="text-slate-400">(You)</span>}
                                {onLeave && (
                                  <Badge className="bg-amber-500 text-white text-xs">
                                    <Palmtree size={10} className="mr-1" />
                                    On Leave
                                  </Badge>
                                )}
                              </p>
                              <p className="text-xs text-slate-500">{member.user_email}</p>
                              <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                                <Clock size={10} />
                                {member.working_hours_start || '09:00'} - {member.working_hours_end || '18:00'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {/* On Leave Toggle */}
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-slate-500">Leave</Label>
                              <Switch
                                checked={onLeave}
                                onCheckedChange={() => handleToggleLeaveStatus(member.user_id, onLeave)}
                                disabled={!canChangeRoles}
                                data-testid={`leave-toggle-${member.user_id}`}
                              />
                            </div>
                            
                            {/* Round Robin Toggle */}
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-slate-500">Round Robin</Label>
                              <Switch
                                checked={roundRobinOn}
                                onCheckedChange={() => handleToggleRoundRobin(member.user_id, roundRobinOn)}
                                disabled={!canChangeRoles}
                                data-testid={`round-robin-toggle-${member.user_id}`}
                              />
                            </div>
                            
                            {/* Edit Button */}
                            {canChangeRoles && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditMemberDialog(member)}
                                className="text-slate-500 hover:text-slate-700"
                              >
                                <Edit2 size={14} />
                              </Button>
                            )}
                            
                            {/* Role Badge/Selector */}
                            {canChangeRoles && !isOwner && !isCurrentUser ? (
                              <Select 
                                value={member.role} 
                                onValueChange={(value) => handleChangeRole(member.user_id, value)}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {roles.filter(r => r.id !== 'owner').map(role => (
                                    <SelectItem key={role.id} value={role.id}>
                                      {role.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge className={`${roleColors[member.role]} text-white`}>
                                {member.role_info?.name || member.role}
                              </Badge>
                            )}
                            
                            {/* Remove Button */}
                            {canRemove && !isOwner && !isCurrentUser && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveMember(member.user_id, member.user_name)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Pending Invitations */}
              {invitations.length > 0 && (
                <Card data-testid="invitations-card">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Mail size={20} />
                      Pending Invitations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {invitations.map((inv) => (
                        <div 
                          key={inv.id}
                          className="flex items-center justify-between p-4 rounded-lg border border-dashed border-slate-300 bg-slate-50"
                        >
                          <div>
                            <p className="font-medium text-sm">{inv.email}</p>
                            <p className="text-xs text-slate-500">
                              Invited as {roles.find(r => r.id === inv.role)?.name || inv.role} by {inv.invited_by_name}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyInviteLink(inv.token)}
                            >
                              {copiedLink === inv.token ? <Check size={14} /> : <Copy size={14} />}
                            </Button>
                            {canInvite && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCancelInvitation(inv.id)}
                                className="text-red-500"
                              >
                                Cancel
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Statistics Tab */}
        <TabsContent value="stats" className="space-y-4">
          {leadsSummary.length > 0 ? (
            <Card data-testid="leads-summary-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 size={20} />
                  Leads by Team Member
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {leadsSummary.map((summary) => (
                    <div 
                      key={summary._id}
                      className="p-4 rounded-lg border border-slate-200"
                    >
                      <p className="font-medium text-sm mb-2">{summary.user_name || 'Unassigned'}</p>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-2xl font-semibold text-blue-600">{summary.leads || 0}</p>
                          <p className="text-xs text-slate-500">Leads</p>
                        </div>
                        <div>
                          <p className="text-2xl font-semibold text-amber-600">{summary.qualified || 0}</p>
                          <p className="text-xs text-slate-500">Qualified</p>
                        </div>
                        <div>
                          <p className="text-2xl font-semibold text-green-600">{summary.customers || 0}</p>
                          <p className="text-xs text-slate-500">Customers</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No lead statistics available yet.</p>
              </CardContent>
            </Card>
          )}

          {/* Role Permissions Info */}
          <Card data-testid="permissions-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings size={20} />
                Role Permissions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {roles.map((role) => {
                  const RoleIcon = roleIcons[role.id] || User;
                  return (
                    <div 
                      key={role.id}
                      className="p-4 rounded-lg border border-slate-200"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 rounded-full ${roleColors[role.id]} flex items-center justify-center`}>
                          <RoleIcon size={14} className="text-white" />
                        </div>
                        <span className="font-medium">{role.name}</span>
                      </div>
                      <p className="text-xs text-slate-500">{role.description}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add User to Team Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="text-accent" size={20} />
              Add User to {selectedTeamForAdd?.name}
            </DialogTitle>
            <DialogDescription>
              Select an existing user and configure their settings
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Select User</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger data-testid="select-user-dropdown">
                  <SelectValue placeholder="Choose a user..." />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableUsers().map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-slate-400" />
                        <span>{u.full_name || u.email}</span>
                        <span className="text-xs text-slate-400">({u.email})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="flex items-center gap-1 mb-2">
                <Globe size={12} />
                Preferred Languages
              </Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-slate-50 min-h-[80px]">
                {availableLanguages.map(lang => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => {
                      if (preferredLanguages.includes(lang.code)) {
                        setPreferredLanguages(preferredLanguages.filter(l => l !== lang.code));
                      } else {
                        setPreferredLanguages([...preferredLanguages, lang.code]);
                      }
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      preferredLanguages.includes(lang.code)
                        ? 'bg-amber-500 text-white shadow-md'
                        : 'bg-white text-slate-600 border border-slate-200 hover:border-amber-300 hover:bg-amber-50'
                    }`}
                    data-testid={`lang-${lang.code}`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
              {preferredLanguages.length > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  Selected: {preferredLanguages.map(code => 
                    availableLanguages.find(l => l.code === code)?.name
                  ).join(', ')}
                </p>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <Clock size={12} />
                  Working Hours Start
                </Label>
                <Input
                  type="time"
                  value={workingHoursStart}
                  onChange={(e) => setWorkingHoursStart(e.target.value)}
                  data-testid="working-hours-start"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <Clock size={12} />
                  Working Hours End
                </Label>
                <Input
                  type="time"
                  value={workingHoursEnd}
                  onChange={(e) => setWorkingHoursEnd(e.target.value)}
                  data-testid="working-hours-end"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <RotateCcw size={16} className="text-slate-500" />
                <Label className="text-sm">Enable Round-Robin</Label>
              </div>
              <Switch
                checked={roundRobinEnabled}
                onCheckedChange={setRoundRobinEnabled}
                data-testid="add-user-round-robin"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Palmtree size={16} className="text-amber-600" />
                <Label className="text-sm">Currently On Leave</Label>
              </div>
              <Switch
                checked={isOnLeave}
                onCheckedChange={setIsOnLeave}
                data-testid="add-user-on-leave"
              />
            </div>
            
            <Button 
              onClick={handleAddUserToTeam} 
              disabled={addingUser || !selectedUserId}
              className="w-full"
              data-testid="submit-add-user-btn"
            >
              {addingUser ? 'Adding...' : 'Add User to Team'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Member Settings Dialog */}
      <Dialog open={showEditMemberDialog} onOpenChange={setShowEditMemberDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="text-accent" size={20} />
              Edit Member Settings
            </DialogTitle>
            <DialogDescription>
              Update working hours and availability for {editingMember?.user_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-1">
                  <Clock size={12} />
                  Working Hours Start
                </Label>
                <Input
                  type="time"
                  value={workingHoursStart}
                  onChange={(e) => setWorkingHoursStart(e.target.value)}
                />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <Clock size={12} />
                  Working Hours End
                </Label>
                <Input
                  type="time"
                  value={workingHoursEnd}
                  onChange={(e) => setWorkingHoursEnd(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <RotateCcw size={16} className="text-slate-500" />
                <Label className="text-sm">Enable Round-Robin</Label>
              </div>
              <Switch
                checked={roundRobinEnabled}
                onCheckedChange={setRoundRobinEnabled}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Palmtree size={16} className="text-amber-600" />
                <Label className="text-sm">Currently On Leave</Label>
              </div>
              <Switch
                checked={isOnLeave}
                onCheckedChange={setIsOnLeave}
              />
            </div>
            
            <div>
              <Label className="flex items-center gap-1 mb-2">
                <Globe size={12} />
                Preferred Languages
              </Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-slate-50 min-h-[60px]">
                {availableLanguages.map(lang => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => {
                      if (preferredLanguages.includes(lang.code)) {
                        setPreferredLanguages(preferredLanguages.filter(l => l !== lang.code));
                      } else {
                        setPreferredLanguages([...preferredLanguages, lang.code]);
                      }
                    }}
                    className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                      preferredLanguages.includes(lang.code)
                        ? 'bg-amber-500 text-white shadow-sm'
                        : 'bg-white text-slate-600 border border-slate-200 hover:border-amber-300'
                    }`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            </div>
            
            <Button 
              onClick={handleUpdateMemberSettings} 
              className="w-full"
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Generated Invitation Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="text-accent" size={20} />
              Invitation Created!
            </DialogTitle>
            <DialogDescription>
              Share this link with your team member to invite them
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <Label className="text-xs text-slate-500 mb-2 block">Shareable Invite Link</Label>
              <div className="flex items-center gap-2">
                <Input 
                  value={generatedLink || ''} 
                  readOnly 
                  className="text-sm font-mono bg-white"
                  data-testid="invite-link-input"
                />
                <Button 
                  onClick={copyGeneratedLink}
                  variant="outline"
                  className="shrink-0"
                  data-testid="copy-link-btn"
                >
                  {copiedLink === 'generated' ? <Check size={16} /> : <Copy size={16} />}
                </Button>
              </div>
            </div>
            <div className="text-sm text-slate-500 space-y-1">
              <p className="flex items-center gap-2">
                <Link2 size={14} />
                Link expires in 7 days
              </p>
              <p className="flex items-center gap-2">
                <Mail size={14} />
                The invited user will need to create an account or log in to join
              </p>
            </div>
            <Button 
              onClick={() => {
                copyGeneratedLink();
                setShowLinkDialog(false);
              }}
              className="w-full bg-accent hover:bg-accent/90"
            >
              <Copy className="mr-2" size={16} />
              Copy Link & Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Team Members Dialog */}
      <Dialog open={showViewTeamDialog} onOpenChange={setShowViewTeamDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="text-accent" size={20} />
              {viewingTeam?.name} - Members
            </DialogTitle>
            <DialogDescription>
              {viewingTeamMembers.length} member(s) in this team
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {viewingTeamMembers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No members in this team yet
              </div>
            ) : (
              viewingTeamMembers.map((member) => {
                const RoleIcon = roleIcons[member.role] || User;
                const onLeave = member.is_on_leave === true;
                const roundRobinOn = member.round_robin_enabled !== false;
                
                return (
                  <div 
                    key={member.id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${onLeave ? 'bg-amber-50 border-amber-200' : 'border-slate-200'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full ${roleColors[member.role] || 'bg-slate-500'} flex items-center justify-center`}>
                        <RoleIcon size={18} className="text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-sm flex items-center gap-2">
                          {member.user_name || member.name || 'Unknown'}
                          {onLeave && (
                            <Badge className="bg-amber-500 text-white text-xs">
                              <Palmtree size={10} className="mr-1" />
                              On Leave
                            </Badge>
                          )}
                        </p>
                        <p className="text-xs text-slate-500">{member.user_email || member.email}</p>
                        <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                          <Clock size={10} />
                          {member.working_hours_start || '09:00'} - {member.working_hours_end || '18:00'}
                        </p>
                        {member.preferred_languages && member.preferred_languages.length > 0 && (
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                            <Globe size={10} />
                            {member.preferred_languages.map(code => {
                              const langMap = {en: 'English', ar: 'Arabic', ru: 'Russian', zh: 'Chinese', hi: 'Hindi', ur: 'Urdu', fr: 'French', es: 'Spanish', de: 'German', pt: 'Portuguese', fa: 'Farsi', tl: 'Tagalog'};
                              return langMap[code] || code;
                            }).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-slate-500 mr-2">
                        Round Robin: {roundRobinOn ? 'On' : 'Off'}
                      </div>
                      <Badge className={`${roleColors[member.role] || 'bg-slate-500'} text-white`}>
                        {member.role_info?.name || member.role}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                        onClick={() => {
                          setShowViewTeamDialog(false);
                          openEditMemberDialog(member);
                        }}
                        data-testid={`edit-member-${member.user_id}`}
                      >
                        <Edit2 size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-slate-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleRemoveFromViewingTeam(member.user_id, member.user_name || member.name)}
                        data-testid={`delete-member-${member.user_id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowViewTeamDialog(false)}
            >
              Close
            </Button>
            <Button
              className="flex-1 bg-accent hover:bg-accent/90"
              onClick={() => {
                setShowViewTeamDialog(false);
                openAddUserDialog(viewingTeam);
              }}
            >
              <UserPlus className="mr-2" size={16} />
              Add User to Team
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagement;
